const TABBY_SCRIPT_SRC = 'https://checkout.tabby.ai/tabby-promo.js';

let ldropTabbyScriptPromise = null;

const ldropLoadTabbyScript = () => {
  if (typeof window.TabbyPromo === 'function') {
    return Promise.resolve();
  }
  if (ldropTabbyScriptPromise != null) {
    return ldropTabbyScriptPromise;
  }
  ldropTabbyScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TABBY_SCRIPT_SRC}"]`);
    if (existing != null) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Tabby script failed to load')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = TABBY_SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Tabby script failed to load')), { once: true });
    document.body.appendChild(script);
  });
  return ldropTabbyScriptPromise;
};

class LdropTabbyWidget extends HTMLElement {
  connectedCallback() {
    this.handleVariantChange = this.handleVariantChange.bind(this);
    document.addEventListener('ldrop:variant-change', this.handleVariantChange);

    ldropLoadTabbyScript()
      .then(() => this.render())
      .catch((error) => console.warn('[ldrop-tabby]', error));
  }

  disconnectedCallback() {
    document.removeEventListener('ldrop:variant-change', this.handleVariantChange);
    this.stopHolding();
  }

  /* Drop the held height once Tabby has rendered into the container (first DOM change, then one frame for its
   * layout), or after a timeout if it never renders. */
  releaseHeldHeight(container) {
    this.stopHolding();
    if (!container.style.getPropertyValue('--ldrop-tabby-hold')) return;

    const release = () => {
      this.stopHolding();
      requestAnimationFrame(() => container.style.removeProperty('--ldrop-tabby-hold'));
    };
    this.holdObserver = new MutationObserver(release);
    this.holdObserver.observe(container, { childList: true, subtree: true });
    this.holdTimer = setTimeout(release, 3000);
  }

  stopHolding() {
    this.holdObserver?.disconnect();
    this.holdObserver = null;
    clearTimeout(this.holdTimer);
  }

  handleVariantChange(event) {
    const variantPrice = event.detail?.variantPrice;
    if (variantPrice == null) return;
    this.dataset.price = String(variantPrice);
    this.render();
  }

  render() {
    if (typeof window.TabbyPromo !== 'function') return;

    const oldContainer = this.querySelector('[data-role="tabby-promo"]');
    if (oldContainer == null) return;

    const price = Number.parseFloat(this.dataset.price ?? '0');
    const installments = Number.parseInt(this.dataset.installments ?? '4', 10);
    const { locale, publicKey, merchantCode, promoSelector } = this.dataset;

    if (!publicKey || !promoSelector) {
      console.warn('[ldrop-tabby] missing public key or selector', { publicKey, promoSelector });
      return;
    }

    const freshContainer = document.createElement('div');
    freshContainer.id = oldContainer.id;
    freshContainer.className = oldContainer.className;
    freshContainer.setAttribute('data-role', 'tabby-promo');
    // the fresh container is empty until Tabby draws into it: keep the old height meanwhile, or the block
    // collapses for a few frames and the layout around it jumps (e.g. a sticky image pinned to the grid bottom)
    const heldHeight = oldContainer.offsetHeight;
    if (heldHeight > 0) freshContainer.style.setProperty('--ldrop-tabby-hold', `${heldHeight}px`);
    oldContainer.replaceWith(freshContainer);
    this.releaseHeldHeight(freshContainer);

    try {
      new window.TabbyPromo({
        selector: promoSelector,
        price,
        locale: locale ?? 'en',
        publicKey,
        merchantCode: merchantCode ?? 'default',
        installmentsCount: installments,
      });
    } catch (error) {
      console.warn('[ldrop-tabby] TabbyPromo init failed', error);
    }
  }
}

if (!customElements.get('ldrop-tabby-widget')) {
  customElements.define('ldrop-tabby-widget', LdropTabbyWidget);
}
